// This file formerly backed the Inspector sidebar (introduced in wandb/core#4666,
// gated behind "INSPECTOR" in the user bio). The Inspector and its concrete
// property editor components have been removed. Only the small set of types and
// helpers still referenced by panel `configSpec` definitions remains here.

import type * as VegaLib2 from '../../util/vega2';
import {type QueryField} from '../../util/vega3';
import {type CustomGradient} from '../GradientPicker';

interface DerivedProp<T> {
  (config: any): T;
  isDerived: true;
}
type WithDerivedProps<T> = {[key in keyof T]: T[key] | DerivedProp<T[key]>};

// shorthand for defining a property editor's spec
interface MakePES<T extends string, V, E> {
  editorType: T;
  valueType: V;
  customProps: E;
  customParams: WithDerivedProps<E> & {editor: T} & (undefined extends V
      ? {default?: V}
      : {default: V});
}

interface IconMenuOption {
  icon: string;
  value: string;
}

// Minimal shape for dropdown options used in panel `configSpec` definitions.
// Previously imported `DropdownItemProps` from `semantic-ui-react`, but that
// package is deprecated in this codebase and the Inspector that rendered these
// options has been removed; this inline type is all that's still needed for
// typing the `configSpec`.
interface DropdownOptionSpec {
  text?: string | number;
  value: string | number | boolean;
}

export interface VegaSpecInfo {
  panelDefId?: string;
  customPanelDef?: VegaLib2.VegaPanelDef;
}

type PropertyEditorSpec =
  | MakePES<
      'string',
      string | undefined,
      {
        displayName: string;
      }
    >
  | MakePES<
      'dropdown',
      string | number,
      {
        displayName: string;
        options: DropdownOptionSpec[];
      }
    >
  | MakePES<
      'number',
      number | undefined,
      {
        displayName: string;
      }
    >
  | MakePES<
      'slider',
      number,
      {
        displayName: string;
        min: number;
        max: number;
        step: number;
      }
    >
  | MakePES<
      'checkbox',
      boolean,
      {
        displayName: string;
      }
    >
  | MakePES<
      'icon-menu',
      string,
      {
        displayName: string;
        options: IconMenuOption[];
      }
    >
  | MakePES<
      'gradient',
      CustomGradient,
      {
        displayName: string;
      }
    >
  | MakePES<
      'query',
      QueryField[],
      {
        fixedFields: QueryField[];
      }
    >
  | MakePES<'vega-spec', VegaSpecInfo, {}>
  | MakePES<
      'vega-mapping',
      {[from: string]: string},
      {query: QueryField[]; vegaSpec: VegaSpecInfo}
    >;

type PropertyEditorParams = PropertyEditorSpec['customParams'] & {
  visible?: boolean | DerivedProp<boolean>;
};

type FilterByEditorType<U, T> = U extends {editorType: T} ? U : never;
type ValueTypeFromEditorType<T> = FilterByEditorType<
  PropertyEditorSpec,
  T
>['valueType'];

export interface PanelConfigSpec {
  [key: string]: PropertyEditorParams;
}

export type PanelConfigSpecToProps<
  T extends {[key in keyof T]: PropertyEditorParams},
> = {
  [k in keyof T]: ValueTypeFromEditorType<T[k]['editor']>;
};

export function derived<T>(derive: (config: any) => T): DerivedProp<T> {
  return Object.assign(derive, {isDerived: true as const});
}
